import { Fragment, memo } from 'react'
import { StrategyInfo } from '../../reducer/strategyBuilder.reducer'
import { Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { toLocaleStringIn } from '../../util/general'
import InfoIcon from '../../assets/icons/InfoIcon'
import { NonInteractiveTooltip } from '../custom'
import { formatLargeNumber } from '../../util/screener'
import { useSelector } from 'react-redux'
import { selectPrimaryTradingAccount } from '../../selectors/user'
import { TradingAccountStatus } from '../../proto/generated/trading-bridge'
import { abs } from 'mathjs'

const formatProfitPercentage = (profit: number, margin: number): string => {
    if (!margin || !isFinite(profit)) return ''
    const percentage = roundToSmartPrecision((profit / margin) * 100)
    return `+${percentage}%`
}

const formatLossPercentage = (loss: number, margin: number): string => {
    if (!margin || !isFinite(loss)) return ''
    const percentage = roundToSmartPrecision((Math.abs(loss) / margin) * 100)
    return `-${percentage}%`
}

const formatBreakevenPercentage = (breakeven: number, spotPrice: number): string => {
    if (!spotPrice) return ''
    const percentage = roundToSmartPrecision(((breakeven - spotPrice) / spotPrice) * 100)
    return `${percentage}%`
}

export function roundToSmartPrecision(number: number) {
    return number.toFixed(abs(number) >= 10 ? 0 : abs(number) >= 1 ? 1 : 2)
}

export type StrategyInfoDisplayProps = {
    info: StrategyInfo
    simulator: boolean
    spotPrice?: number
}

const StrategyInfoDisplay: React.FC<StrategyInfoDisplayProps> = ({ info: { maxProfit, maxLoss, totalMargin, delta, theta, pop, breakeven }, simulator, spotPrice }) => {
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)

    return (
        <Stack direction={'row'} sx={{ display: 'flex', gap: 4 }}>
            <Grid item>
                <Grid item xs={12} textAlign={'center'}>
                    <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                        MAX PROFIT
                    </Typography>
                </Grid>
                <Grid item xs={12} textAlign={'center'} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography className="green-text" variant="body1" component="span" color="primaryText" fontWeight={500} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {maxProfit
                            ? toLocaleStringIn(Math.round(maxProfit), {
                                  maximumFractionDigits: 0,
                                  minimumFractionDigits: 0
                              })
                            : '-'}
                    </Typography>
                    <span style={{ fontSize: '12px', lineHeight: '10px', opacity: 0.7 }} className="green-text">
                        {formatProfitPercentage(maxProfit, totalMargin)}
                    </span>
                </Grid>
            </Grid>
            <Grid item>
                <Grid item xs={12} textAlign={'center'}>
                    <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                        MAX LOSS
                    </Typography>
                </Grid>
                <Grid item xs={12} textAlign={'center'} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography className="red-text" variant="body1" component="span" color="primaryText" fontWeight={500} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {maxLoss
                            ? toLocaleStringIn(maxLoss, {
                                  maximumFractionDigits: 0,
                                  minimumFractionDigits: 0
                              })
                            : '-'}
                    </Typography>
                    <span style={{ fontSize: '12px', lineHeight: '10px', opacity: 0.7 }} className="red-text">
                        {formatLossPercentage(maxLoss, totalMargin)}
                    </span>
                </Grid>
            </Grid>
            <Grid item>
                <Grid item xs={12} textAlign={'center'}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                            MARGIN
                        </Typography>
                        {simulator && (
                            <Tooltip title="Margin calculation is not available in simulator">
                                <IconButton size="small" sx={{ padding: 0 }}>
                                    <InfoIcon sx={{ fontSize: '0.75rem' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                </Grid>
                <Grid item xs={12} textAlign={'center'}>
                    {!simulator && (!primaryTradingAccount || primaryTradingAccount.status !== TradingAccountStatus.ACTIVE) ? (
                        <Typography sx={{ p: 0 }} className="trade-panel-bold-text" variant="body1" component="span" color="primaryText" fontSize={'small'}>
                            —
                        </Typography>
                    ) : (
                        <NonInteractiveTooltip title={totalMargin ? toLocaleStringIn(totalMargin) : null} placement="top" arrow>
                            <Typography sx={{ p: 0 }} className="trade-panel-bold-text" variant="body1" component="span" color="primaryText" fontSize={'small'}>
                                {totalMargin ? formatLargeNumber(totalMargin) : '-'}
                            </Typography>
                        </NonInteractiveTooltip>
                    )}
                </Grid>
            </Grid>
            <Grid item>
                <Grid item xs={12} textAlign={'center'}>
                    <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                        DELTA
                    </Typography>
                </Grid>
                <Grid item xs={12} textAlign={'center'}>
                    <NonInteractiveTooltip title={delta ? formatLargeNumber(delta) : null} placement="top" arrow>
                        <Typography className="trade-panel-bold-text" variant="body1" component="span" color="primaryText" fontSize={'small'}>
                            {delta ? formatLargeNumber(delta) : '-'}
                        </Typography>
                    </NonInteractiveTooltip>
                </Grid>
            </Grid>
            <Grid item>
                <Grid item xs={12} textAlign={'center'}>
                    <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                        THETA
                    </Typography>
                </Grid>
                <Grid item xs={12} textAlign={'center'}>
                    <NonInteractiveTooltip title={theta ? formatLargeNumber(theta) : null} placement="top" arrow>
                        <Typography className="trade-panel-bold-text" variant="body1" component="span" color="primaryText" fontSize={'small'}>
                            {theta ? formatLargeNumber(theta) : '-'}
                        </Typography>
                    </NonInteractiveTooltip>
                </Grid>
            </Grid>
            <Grid item>
                <Grid item xs={12} textAlign={'center'}>
                    <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                        POP
                    </Typography>
                </Grid>
                <Grid item xs={12} textAlign={'center'}>
                    <Typography className="trade-panel-bold-text" variant="body1" component="span" color="primaryText" fontSize={'small'}>
                        {pop ? `${pop.toFixed(0)}%` : '-'}
                    </Typography>
                </Grid>
            </Grid>
            <Grid item>
                <Grid item xs={12} textAlign={'center'}>
                    <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                        BREAKEVEN
                    </Typography>
                </Grid>
                <Grid item xs={12} textAlign={'center'}>
                    <Typography
                        className="trade-panel-bold-text"
                        variant="body1"
                        component="span"
                        color="primaryText"
                        fontSize={'small'}
                        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '2px' }}
                    >
                        {!breakeven || !breakeven.length
                            ? '-'
                            : breakeven.map((curr: number, index: number) => {
                                  const percentage = spotPrice ? formatBreakevenPercentage(curr, spotPrice) : ''
                                  return (
                                      <Fragment key={index}>
                                          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                              <span style={{ lineHeight: '24px' }}>{curr.toFixed(0)}</span>
                                              <span style={{ fontSize: '12px', lineHeight: '10px', opacity: 0.7 }}>{percentage}</span>
                                          </span>
                                          {index < breakeven.length - 1 ? <span style={{ fontSize: '12px', lineHeight: '10px' }}> </span> : null}
                                      </Fragment>
                                  )
                              })}
                    </Typography>
                </Grid>
            </Grid>
        </Stack>
    )
}

export default memo(StrategyInfoDisplay)
